import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";
import DOMPurify from "dompurify";
import SEO from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { getBlogViewFingerprint } from "@/lib/blogViewFingerprint";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  author: string;
  published_at: string;
  image_url: string;
  view_count?: number;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewCount, setViewCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      
      // No .lte('published_at',...) here: RLS already hides scheduled posts
      // from non-admins, while admins can fetch them to preview.
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      
      if (!error && data) {
        setPost(data);
        setViewCount(typeof data.view_count === 'number' ? data.view_count : 0);

        // Only count views for posts that are actually published.
        if (new Date(data.published_at).getTime() <= Date.now()) {
          const fingerprint = getBlogViewFingerprint();
          if (fingerprint) {
            supabase
              .rpc('record_blog_post_view', { _slug: slug, _fingerprint: fingerprint })
              .then(({ data: newCount, error: rpcError }) => {
                if (!rpcError && typeof newCount === 'number') {
                  setViewCount(newCount);
                }
              });
          }
        }
      }
      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  const slugToTitle = (s: string) => {
    return s
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return (
      <>
        <SEO
          title={`Blog | Collectif Ibbü — ${slugToTitle(slug || '')}`}
          description="Chargement de l'article..."
          canonical={`https://collectif-ibbu.org/blog/${slug}`}
        />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <SEO
          title="Article non trouvé | Blog Collectif Ibbü"
          description="Cet article du blog du Collectif Ibbü n'existe pas ou a été supprimé."
          canonical="https://collectif-ibbu.org/blog"
        />
        <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Article non trouvé</h1>
            <p className="text-muted-foreground mb-6">
              Cet article n'existe pas ou a été supprimé.
            </p>
            <Button asChild>
              <Link to="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour au blog
              </Link>
            </Button>
          </div>
        </div>
        <Footer />
        </div>
      </>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.content.substring(0, 160),
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "datePublished": post.published_at,
    "publisher": {
      "@type": "Organization",
      "name": "Collectif Ibbü"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://collectif-ibbu.org/blog/${post.slug}`
    },
    ...(post.image_url && {
      "image": post.image_url.startsWith('http') ? post.image_url : `https://collectif-ibbu.org${post.image_url}`
    })
  };

  const absoluteOgImage = post.image_url
    ? (post.image_url.startsWith('http') ? post.image_url : `https://collectif-ibbu.org${post.image_url}`)
    : undefined;

  return (
    <>
      <SEO
        title={`${post.title} | Blog Collectif Ibbü`}
        description={post.content.replace(/<[^>]*>/g, '').substring(0, 160)}
        canonical={`https://collectif-ibbu.org/blog/${post.slug}`}
        ogImage={absoluteOgImage}
        ogType="article"
        structuredData={structuredData}
        keywords={`Ibbü, Ibbu, iAdvize, conseillers, ${post.title}`}
      />
      <div className="min-h-screen flex flex-col">
      <main className="flex-1">
        <article className="max-w-4xl mx-auto px-4 py-12">
          <Breadcrumbs items={[
            { label: "Blog", href: "/blog" },
            { label: post.title }
          ]} />
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au blog
            </Link>
          </Button>

          {new Date(post.published_at) > new Date() && (
            <div
              role="status"
              className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
            >
              📅 <strong>Aperçu — article planifié.</strong> Publication automatique le{' '}
              {new Date(post.published_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}.
              Cet article n'est visible que par les administrateurs jusqu'à cette date.
            </div>
          )}

          {post.image_url && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img 
                src={post.image_url} 
                alt={post.title}
                width={1200}
                height={630}
                fetchPriority="high"
                decoding="async"
                className="w-full h-96 object-cover"
              />
            </div>
          )}

          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground">
              <span>Par {post.author}</span>
              <span aria-hidden="true">•</span>
              <time>
                {new Date(post.published_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </time>
              {viewCount !== null && viewCount > 0 && (
                <>
                  <span aria-hidden="true">•</span>
                  <span
                    className="inline-flex items-center gap-1.5"
                    aria-label={`${viewCount} ${viewCount > 1 ? 'vues' : 'vue'}`}
                    title="Vues uniques (déduplication 24 h)"
                  >
                    <Eye className="h-4 w-4" aria-hidden="true" />
                    {viewCount.toLocaleString('fr-FR')}
                  </span>
                </>
              )}
              <span aria-hidden="true">•</span>
              <ShareButtons
                url={`https://collectif-ibbu.org/partage/blog/${post.slug}.html`}
                title={post.title}
                description={post.content.replace(/<[^>]*>/g, '').substring(0, 160)}
              />
            </div>
          </header>

          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />

        </article>
      </main>
      <Footer />
      </div>
    </>
  );
};

export default BlogPost;